import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash } from "@phosphor-icons/react";
import { Header, IconBtn } from "./shared";

export default function RegistrationsTab() {
  const [regs, setRegs] = useState([]);
  const load = useCallback(() => api.get("/registrations").then((r) => setRegs(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => { await api.put(`/registrations/${id}`, { status }); toast.success(`Marked ${status}`); load(); };
  const togglePaid = async (id, paid) => { await api.put(`/registrations/${id}`, { paid }); toast.success(paid ? "Marked as paid" : "Marked as unpaid"); load(); };
  const del = async (id) => { if (!window.confirm("Delete registration?")) return; await api.delete(`/registrations/${id}`); toast.success("Deleted"); load(); };
  const convert = async (r) => {
    if (!window.confirm(`Create team "${r.team_name || r.name}" with ${(r.roster || []).length} player(s)?`)) return;
    try {
      const res = await api.post(`/registrations/${r.id}/convert`);
      toast.success(`Team created with ${res.data.players_created} player(s)`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const badge = { pending: "bg-[#FFEA00]/20 text-[#FFEA00]", approved: "bg-[#00E676]/20 text-[#00E676]", rejected: "bg-[#FF3B30]/20 text-[#FF3B30]" };

  return (
    <div data-testid="registrations-tab">
      <Header title="Registrations" />
      <div className="space-y-3">
        {regs.map((r) => (
          <div key={r.id} className="border border-white/10 bg-[#121212] p-5" data-testid="registration-row">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-3">
                  <span className="font-display text-xl uppercase">{r.name}</span>
                  <span className="text-xs uppercase tracking-wide bg-white/10 px-2 py-0.5">{r.type}</span>
                  <span className={`text-xs uppercase tracking-wide px-2 py-0.5 ${badge[r.status]}`}>{r.status}</span>
                  <span data-testid="paid-badge" className={`text-xs uppercase tracking-wide px-2 py-0.5 ${r.paid ? "bg-[#00E676]/20 text-[#00E676]" : "bg-white/10 text-white/50"}`}>{r.paid ? "Paid" : "Unpaid"}</span>
                </div>
                <div className="text-white/50 text-sm mt-1">{r.email} {r.phone && `· ${r.phone}`}</div>
                {r.team_name && <div className="text-white/40 text-sm">Team: {r.team_name}</div>}
                <div className="text-white/40 text-sm">Division: {r.division || "Open"}</div>
                {r.position && <div className="text-white/40 text-sm">Position: {r.position}</div>}
                {r.roster && r.roster.length > 0 && (
                  <div className="mt-2" data-testid="registration-roster">
                    <div className="text-white/50 text-xs uppercase tracking-wide mb-1">Roster ({r.roster.length})</div>
                    <div className="flex flex-wrap gap-1.5">
                      {r.roster.map((p, idx) => (
                        <span key={idx} className="text-xs bg-white/5 border border-white/10 px-2 py-1">
                          {p.number != null ? <span className="text-[#FF5722]">#{p.number} </span> : null}{p.name}{p.position ? <span className="text-white/40"> · {p.position}</span> : null}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {r.message && <div className="text-white/40 text-sm mt-1 italic">"{r.message}"</div>}
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="toggle-paid-btn" onClick={() => togglePaid(r.id, !r.paid)} className={`text-xs uppercase font-bold px-3 py-2 border transition-colors ${r.paid ? "border-white/20 text-white/60 hover:bg-white/5" : "border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/10"}`}>
                  {r.paid ? "Mark Unpaid" : "Mark Paid"}
                </button>
                {r.type === "team" && (
                  <button data-testid="convert-team-btn" onClick={() => convert(r)} className="text-xs uppercase font-bold px-3 py-2 bg-[#FF5722] text-black hover:bg-[#FF7043] transition-colors">Approve &amp; Create Team</button>
                )}
                <button data-testid="approve-reg-btn" onClick={() => setStatus(r.id, "approved")} className="text-xs uppercase font-bold px-3 py-2 border border-[#00E676]/40 text-[#00E676] hover:bg-[#00E676]/10 transition-colors">Approve</button>
                <button data-testid="reject-reg-btn" onClick={() => setStatus(r.id, "rejected")} className="text-xs uppercase font-bold px-3 py-2 border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors">Reject</button>
                <IconBtn onClick={() => del(r.id)} danger testid="delete-reg-btn"><Trash size={18} /></IconBtn>
              </div>
            </div>
          </div>
        ))}
        {regs.length === 0 && <p className="text-white/40">No registrations yet.</p>}
      </div>
    </div>
  );
}
